"use client";
import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
    Form,
    FormField,
    FormItem,
    FormLabel,
    FormControl,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cbChangePassword } from "@/services/auth.service";
import { userChangePasswordSchema, UserChangePasswordInput } from "@/schemas/user-change-password.schema";

interface ChangePasswordFormProps {
    email?: string;
    open?: boolean;
    onSuccess?: () => void;
    onClose?: () => void;
}

export default function ChangePasswordForm({ email = "", onSuccess, onClose: _onClose }: ChangePasswordFormProps) {
    const form = useForm<UserChangePasswordInput>({
        resolver: zodResolver(userChangePasswordSchema),
        defaultValues: {
            email,
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
        },
    });
    const [message, setMessage] = React.useState<string | null>(null);

    const onSubmit = async (values: UserChangePasswordInput) => {
        setMessage(null);
        form.clearErrors();
        try {
            const data = await cbChangePassword({
                email: values.email,
                currentPassword: values.currentPassword,
                newPassword: values.newPassword,
                confirmPassword: values.confirmPassword,
            });
            setMessage(typeof data === "string" ? data : "Password changed successfully.");
            form.reset();
            if (onSuccess) onSuccess();
        } catch (error: unknown) {
            // Axios error: error.response?.data for API error, error.message for network error
            // const apiError = error as AxiosError;
            // form.setError("root", { message: (apiError?.response?.data?.error || apiError?.response?.data?.message || error?.message || "Failed to change password.") });
        }
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                                <Input type="email" autoComplete="email" {...field} />
                            </FormControl>
                            <FormMessage>{form.formState.errors.email?.message}</FormMessage>
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="currentPassword"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Current Password</FormLabel>
                            <FormControl>
                                <Input type="password" autoComplete="current-password" {...field} />
                            </FormControl>
                            <FormMessage>{form.formState.errors.currentPassword?.message}</FormMessage>
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="newPassword"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>New Password</FormLabel>
                            <FormControl>
                                <Input type="password" autoComplete="new-password" {...field} />
                            </FormControl>
                            <FormMessage>{form.formState.errors.newPassword?.message}</FormMessage>
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Confirm New Password</FormLabel>
                            <FormControl>
                                <Input type="password" autoComplete="new-password" {...field} />
                            </FormControl>
                            <FormMessage>{form.formState.errors.confirmPassword?.message}</FormMessage>
                        </FormItem>
                    )}
                />
                <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
                    {form.formState.isSubmitting ? "Changing..." : "Change Password"}
                </Button>
                {message && <div className="mt-4 text-green-600">{message}</div>}
                {form.formState.errors.root && <div className="mt-4 text-red-600">{(form.formState.errors.root as { message?: string })?.message}</div>}
            </form>
        </Form>
    );
}
